"use client";

import { useEffect } from "react";
import { useParams, usePathname } from "next/navigation";
import useUserInfo from "@/hooks/useUserInfo";
import { isAuthPath } from "@/components/IntercomProvider";
import { clearSentryUser, setSentryUser } from "@/utils/sentryClientErrorTracking";

/**
 * Tags this tab's Sentry events with the signed-in user and the company being viewed, so an issue
 * shows which client hit it (search `companyId:<id>` in Sentry). It reads the same cached
 * /api/user/me as IntercomProvider, so it costs no extra request, and on sign-in pages it reads
 * nothing.
 */
export default function SentryUserScope() {
  const pathname = usePathname();
  const params = useParams();
  const { userId, userName, email, role, companyIds } = useUserInfo({
    enabled: !isAuthPath(pathname),
  });

  // The company in the URL; on pages without one (e.g. /home), the user's only company if they have one.
  const routeCompanyId = typeof params?.companyId === "string" ? params.companyId : undefined;
  const companyId = routeCompanyId ?? (companyIds.length === 1 ? companyIds[0] : undefined);

  useEffect(() => {
    if (!userId) {
      clearSentryUser();
      return;
    }
    setSentryUser({
      id: userId,
      email: email ?? undefined,
      username: userName ?? undefined,
      role: role || undefined,
      companyId,
    });
  }, [userId, userName, email, role, companyId]);

  return null;
}
